import { Box, CircularProgress, TextField } from '@mui/material';
import {
  AutocompleteCloseReason,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import classNames from 'classnames';
import { Ref, forwardRef, useRef } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import NakedInput from '../../../../modules/NakedInput';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { isFalsyOrEmptyArray } from '../../../../utils/isFalsyOrEmptyArray';
import useAutocomplete from '../../../Hooks/useAutocomplete';
import AutocompleteMuiAdapter from '../../../adaptersMui/AutocompleteMuiAdapter/AutocompleteMuiAdapter';
import renderDefaultOptions from './RenderDefaultOptions';
import AutocompleteChipsRenderer from './components/AutocompleteChipsRenderer/AutocompleteChipsRenderer';
import { useDefaultAutocompleteOptionsStyles } from './styles';
import { InternalAutoCompleteProps } from './types/autocomplete.types';
import { handleChangeAutocompleteInput } from './utils/autocomplete.utils';

const defaultGetOptionSelected = (a: any, b: any) => {
  if (!a || !b) {
    return false;
  }
  const hasSameId = a.id && b.id && a.id === b.id;
  const hasSameValue = a.value && b.value && a.value === b.value;
  return hasSameId || hasSameValue;
};

/**
 * @deprecated Use 'Autocomplete' from 'src/components/v2/form/Autocomplete/Autocomplete' instead.
 */
function AutoComplete<T = any>(
  {
    onInputChangeDelay,
    onInputChangeDelayed,
    onInputChange,
    label,
    getOptionLabel,
    onChangeInputText,
    classes,
    className,
    tagClasses,
    getOptionSelected,
    size,
    helperText,
    renderInput,
    onCloseAutocomplete,
    error,
    startAdornment,
    renderOption,
    onClear,
    disableChipDelete,
    allowNew = false,
    disableFilterOptions = false,
    disableClearable = false,
    isNaked = false,
    textFieldProps,
    checkbox = false,
    value,
    options: rawOptions,
    shouldLimitTags = true,
    onOpen,
    onClose,
    filterOptions,
    renderTags,
    valuesWithError,
    isSelectedValuesWithError,
    ...rest
  }: InternalAutoCompleteProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const { translate } = useTranslation();
  const { classes: defaultOptionsClasses } = useDefaultAutocompleteOptionsStyles();
  const internalInputRef = useRef<HTMLInputElement>(null);

  const handleGetOptionLabel = (option: T | string) => {
    if (typeof option === 'string') {
      return getOptionLabel ? getOptionLabel(option as T) : option;
    }

    if (isFalsyOrEmptyArray(option) || !getOptionLabel(option)) {
      return '';
    }

    return getOptionLabel(option);
  };

  const {
    chipContainerRef,
    debouncedInputChange,
    displayedOptions,
    forceLimitTagsToOne,
    inputText,
    limitTags,
    normalizedValue,
    handleCloseAutocomplete,
    setIsInputOpen,
    isInputOpen,
    handleChangeInputText,
  } = useAutocomplete<T>({
    onChangeInputText,
    multiple: rest.multiple,
    options: rawOptions,
    value: value as T[] | T,
    getOptionLabel: handleGetOptionLabel,
    onCloseAutocomplete,
    onInputChangeDelayed,
    onClear,
  });

  const TextFieldComponent = isNaked ? NakedInput(TextField) : TextField;

  const renderDefaultInput = (params: AutocompleteRenderInputParams) => (
    <TextFieldComponent
      {...params}
      inputRef={internalInputRef}
      slotProps={{
        input: {
          ...params.InputProps,
          startAdornment: startAdornment || params.InputProps.startAdornment,
          endAdornment: (
            <>
              {rest.loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        },
        inputLabel: isSelectedValuesWithError
          ? {
              sx: {
                color: 'primary.main',
                '&.Mui-focused': {
                  color: 'primary.main',
                },
              },
            }
          : undefined,
      }}
      label={label}
      helperText={helperText}
      error={error || isSelectedValuesWithError}
      variant="outlined"
      size="small"
      {...textFieldProps}
    />
  );

  const handleOpen = () => {
    if (!isInputOpen) {
      setIsInputOpen(true);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();

    if (rest.disabled) {
      return;
    }

    internalInputRef.current?.focus();
    handleOpen();
  };

  const renderDefaultTags = (value: readonly T[], getTagProps: AutocompleteRenderGetTagProps) => (
    <AutocompleteChipsRenderer
      value={value}
      getTagProps={getTagProps}
      getLimitTags={getLimitTags}
      handleGetOptionLabel={handleGetOptionLabel}
      tagClasses={tagClasses}
      defaultOptionsClasses={defaultOptionsClasses}
      size={size === 'large' ? 'medium' : size}
      handleToggleInput={() => {
        internalInputRef.current?.focus();
        setIsInputOpen((prevState) => !prevState);
      }}
      valuesWithError={valuesWithError}
      isOptionEqualToValue={(option: T, value: T) => {
        return getOptionSelected?.(option, value) || defaultGetOptionSelected(option, value);
      }}
    />
  );

  const getLimitTags = () => {
    if (!shouldLimitTags) {
      return;
    }
    return forceLimitTagsToOne ? 1 : limitTags;
  };

  return (
    <Box sx={{ cursor: rest.disabled ? 'default' : 'pointer' }} onClick={handleClick}>
      <AutocompleteMuiAdapter
        clearOnEscape
        ref={ref || chipContainerRef}
        value={normalizedValue}
        limitTags={renderTags ? getLimitTags() : undefined}
        classes={{ option: defaultOptionsClasses.option, ...classes }}
        isOptionEqualToValue={(option: T, value: T) => {
          return getOptionSelected?.(option, value) || defaultGetOptionSelected(option, value);
        }}
        noOptionsText={translate(LangEnum.GLOBAL_NO_OPTIONS)}
        loadingText={translate(LangEnum.GLOBAL_SEARCHING)}
        disableClearable={disableClearable}
        inputValue={inputText}
        options={displayedOptions}
        size={size}
        multiple={false}
        {...rest}
        className={className}
        getOptionLabel={handleGetOptionLabel}
        onInputChange={(evt, value, reason) =>
          handleChangeAutocompleteInput({
            inputParams: { evt, value, reason },
            checkbox,
            multiple: rest.multiple ?? false,
            onChangeInputText: (value: string) => handleChangeInputText(value),
            debouncedInputChange,
            onInputChange,
            onClear,
          })
        }
        onOpen={(event) => {
          onOpen?.(event);
        }}
        slotProps={{
          listbox: {
            style: {
              maxHeight: '350px',
              overflowY: 'auto',
            },
            ...rest.slotProps?.listbox,
          },
          chip: {
            className: classNames({
              [defaultOptionsClasses.limitedChip]: getLimitTags() === 1 && forceLimitTagsToOne,
            }),
            ...rest.slotProps?.chip,
          },
          clearIndicator: {
            sx: { color: 'action.active' },
            ...rest.slotProps?.clearIndicator,
          },
          popupIndicator: {
            sx: { color: 'action.active', mr: (theme) => theme.spacing(0.25) },
            ...rest.slotProps?.popupIndicator,
          },
        }}
        open={isInputOpen}
        onClose={(event, reason: AutocompleteCloseReason) => {
          handleCloseAutocomplete();
          onClose?.(event, reason);
        }}
        renderOption={(option, state) =>
          renderDefaultOptions({
            option,
            checkbox,
            state,
            getOptionLabel: handleGetOptionLabel,
            renderOption,
          })
        }
        filterOptions={(options, params): T[] => {
          if (!!filterOptions) {
            return filterOptions(options, params);
          }
          if (params.inputValue.length > 0) {
            const mutableOptions = [...options];
            return createFilterOptions<T>()(mutableOptions, params);
          }
          const mutableDisplayedOptions = [...displayedOptions];
          return createFilterOptions<T>()(mutableDisplayedOptions, params);
        }}
        renderInput={renderInput || renderDefaultInput}
        renderTags={renderTags || renderDefaultTags}
      />
    </Box>
  );
}

export default forwardRef(AutoComplete) as <T>(
  props: InternalAutoCompleteProps<T>,
  ref: Ref<HTMLInputElement>,
) => React.ReactElement;
